import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { filter, finalize } from 'rxjs';
import { CurrentContextService } from '../../../core/context/current-context.service';
import { LiveUpdateEventTypes, growthPaidMediaTopic } from '../../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../../core/live-updates/live-update.service';
import { LocalizationService } from '../../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../../shared/design-system/page.component';
import { AinStateComponent } from '../../../shared/design-system/state.component';
import { GrowthCampaign, GrowthMarketingService } from '../growth-marketing.service';
import {
  GrowthPaidMediaAccountDto,
  GrowthPaidMediaCampaignLinkDto,
  accountDisplayLabel
} from './growth-paid-media.models';
import { GrowthPaidMediaService } from './growth-paid-media.service';

@Component({
  standalone: true,
  selector: 'ain-growth-paid-media-campaign-link-detail',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    AinFormFieldComponent,
    RouterLink,
    FormsModule,
    ButtonModule,
    InputTextModule,
    InputNumberModule,
    DialogModule
  ],
  template: `
    <ain-page
      [title]="campaignName() || t('growth.paidMedia.campaignLinkDetail.title')"
      [description]="t('growth.paidMedia.campaignLinkDetail.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.paidMedia.campaignLinkDetail.help.title'"
        [descriptionKey]="'growth.paidMedia.campaignLinkDetail.help.description'"
        [bulletKeys]="['growth.paidMedia.campaignLinkDetail.help.bullets.submit', 'growth.paidMedia.campaignLinkDetail.help.bullets.approve']" />

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (!link()) {
        <ain-state type="empty" [title]="t('growth.paidMedia.campaignLinkDetail.messages.notFound')" />
      } @else {
        <div class="toolbar">
          <a class="back-link" [routerLink]="['/growth/paid-media/campaign-links']">{{ t('catalog.action.backToList') }}</a>
          <div class="toolbar-actions">
            @if (canSubmit()) {
              <button
                pButton
                type="button"
                [label]="t('growth.paidMedia.campaignLinkDetail.actions.submit')"
                (click)="openSubmitDialog()"></button>
            }
            @if (canApprove()) {
              <button
                pButton
                type="button"
                [label]="t('growth.paidMedia.campaignLinkDetail.actions.approve')"
                (click)="openApproveDialog()"></button>
            }
            <a class="link-btn" [routerLink]="['/growth/paid-media/roas']" [queryParams]="{ campaignLinkId: linkId }">
              {{ t('growth.paidMedia.campaignLinkDetail.openRoas') }}
            </a>
          </div>
        </div>

        <section class="card">
          <h3>{{ t('growth.paidMedia.campaignLinkDetail.sections.summary') }}</h3>
          <ain-info-hint
            [titleKey]="'growth.paidMedia.campaignLinkDetail.help.summary.title'"
            [descriptionKey]="'growth.paidMedia.campaignLinkDetail.help.summary.description'" />
          <div class="grid">
            <label>
              <span>{{ t('growth.paidMedia.campaignLinks.fields.campaign') }}</span>
              <input pInputText type="text" [ngModel]="campaignName()" [disabled]="true" />
            </label>
            <label>
              <span>{{ t('growth.paidMedia.campaignLinks.fields.account') }}</span>
              <input pInputText type="text" [ngModel]="accountName()" [disabled]="true" />
            </label>
            <label>
              <span>{{ t('growth.paidMedia.campaignLinks.fields.objective') }}</span>
              <input pInputText type="text" [ngModel]="objectiveLabel(link()!.objective)" [disabled]="true" />
            </label>
            <label>
              <span>{{ t('growth.paidMedia.campaignLinks.fields.budgetCap') }}</span>
              <input pInputText type="text" [ngModel]="link()!.budgetCap" [disabled]="true" />
            </label>
            <label>
              <span>{{ t('growth.paidMedia.campaignLinks.fields.requestedSpend') }}</span>
              <input pInputText type="text" [ngModel]="link()!.requestedSpend ?? '-'" [disabled]="true" />
            </label>
            <label>
              <span>{{ t('growth.paidMedia.campaignLinks.columns.approvalState') }}</span>
              <input pInputText type="text" [ngModel]="approvalLabel(link()!.approvalState)" [disabled]="true" />
            </label>
            <label>
              <span>{{ t('growth.paidMedia.campaignLinks.columns.status') }}</span>
              <input pInputText type="text" [ngModel]="link()!.status" [disabled]="true" />
            </label>
            <label>
              <span>{{ t('growth.paidMedia.campaignLinks.fields.externalCampaignId') }}</span>
              <input pInputText type="text" [ngModel]="link()!.externalCampaignId || '-'" [disabled]="true" />
            </label>
            <label class="full">
              <span>{{ t('growth.paidMedia.campaignLinks.fields.approvalReason') }}</span>
              <input pInputText type="text" [ngModel]="link()!.approvalReason || '-'" [disabled]="true" />
            </label>
          </div>
        </section>

        @if (errorMessage()) {
          <p class="message error">{{ errorMessage() }}</p>
        }
        @if (successMessage()) {
          <p class="message success">{{ successMessage() }}</p>
        }
      }
    </ain-page>

    <p-dialog
      [header]="t('growth.paidMedia.campaignLinkDetail.submitDialog.title')"
      [visible]="submitDialogVisible()"
      (visibleChange)="submitDialogVisible.set($event)"
      [modal]="true">
      <ain-info-hint
        [titleKey]="'growth.paidMedia.campaignLinkDetail.submitDialog.help.title'"
        [descriptionKey]="'growth.paidMedia.campaignLinkDetail.submitDialog.help.description'" />
      <ain-form-field [label]="t('growth.paidMedia.campaignLinks.fields.requestedSpend')">
        <p-inputNumber [(ngModel)]="requestedSpend" [min]="0" mode="decimal" [minFractionDigits]="0" [maxFractionDigits]="2" />
      </ain-form-field>
      <p class="hint">{{ t('growth.paidMedia.campaignLinkDetail.submitDialog.budgetHint') }}: {{ link()?.budgetCap ?? '-' }}</p>
      <ng-template pTemplate="footer">
        <button pButton type="button" [label]="t('common.cancel')" severity="secondary" (click)="submitDialogVisible.set(false)"></button>
        <button
          pButton
          type="button"
          [label]="t('growth.paidMedia.campaignLinkDetail.actions.submit')"
          [loading]="actionType() === 'submit'"
          (click)="confirmSubmit()"></button>
      </ng-template>
    </p-dialog>

    <p-dialog
      [header]="t('growth.paidMedia.campaignLinkDetail.approveDialog.title')"
      [visible]="approveDialogVisible()"
      (visibleChange)="approveDialogVisible.set($event)"
      [modal]="true">
      <ain-info-hint
        [titleKey]="'growth.paidMedia.campaignLinkDetail.approveDialog.help.title'"
        [descriptionKey]="'growth.paidMedia.campaignLinkDetail.approveDialog.help.description'" />
      <ain-form-field [label]="t('growth.paidMedia.campaignLinks.fields.approvalReason')">
        <input pInputText type="text" [(ngModel)]="approveReason" />
      </ain-form-field>
      <ng-template pTemplate="footer">
        <button pButton type="button" [label]="t('common.cancel')" severity="secondary" (click)="approveDialogVisible.set(false)"></button>
        <button
          pButton
          type="button"
          [label]="t('growth.paidMedia.campaignLinkDetail.actions.approve')"
          [loading]="actionType() === 'approve'"
          (click)="confirmApprove()"></button>
      </ng-template>
    </p-dialog>
  `,
  styles: [`
    .toolbar { margin-bottom: 12px; display: flex; justify-content: space-between; align-items: center; gap: 8px; flex-wrap: wrap; }
    .toolbar-actions { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .back-link { color: var(--ain-color-primary); font-weight: 600; text-decoration: none; }
    .link-btn { color: #1d4ed8; font-weight: 600; text-decoration: none; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; margin-bottom: 12px; display: grid; gap: 10px; }
    .card h3 { margin: 0; }
    .grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
    label { display: grid; gap: 6px; }
    label.full { grid-column: 1 / -1; }
    .hint { margin: 8px 0 0; color: #64748b; font-size: 13px; }
    .message.error { color: #b91c1c; }
    .message.success { color: #15803d; }
    @media (max-width: 960px) { .grid { grid-template-columns: 1fr 1fr; } }
    @media (max-width: 640px) { .grid { grid-template-columns: 1fr; } }
  `]
})
export class GrowthPaidMediaCampaignLinkDetailComponent implements OnInit {
  private readonly api = inject(GrowthPaidMediaService);
  private readonly marketing = inject(GrowthMarketingService);
  private readonly localization = inject(LocalizationService);
  private readonly route = inject(ActivatedRoute);
  private readonly context = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly liveUpdateService = inject(LiveUpdateService);

  readonly loading = signal(true);
  readonly link = signal<GrowthPaidMediaCampaignLinkDto | null>(null);
  readonly accounts = signal<GrowthPaidMediaAccountDto[]>([]);
  readonly campaigns = signal<GrowthCampaign[]>([]);
  readonly errorMessage = signal('');
  readonly successMessage = signal('');
  readonly actionType = signal<'submit' | 'approve' | null>(null);
  readonly submitDialogVisible = signal(false);
  readonly approveDialogVisible = signal(false);

  linkId = '';
  requestedSpend = 0;
  approveReason = '';
  private subscribedTopic: string | null = null;

  ngOnInit(): void {
    this.linkId = this.route.snapshot.paramMap.get('linkId') ?? '';

    this.liveUpdateService.events$
      .pipe(
        filter(event => {
          if (event.eventType !== LiveUpdateEventTypes.growthPaidMediaChanged) {
            return false;
          }
          const payloadId = event.payload['campaignLinkId'];
          return payloadId === undefined || payloadId === null || String(payloadId).toLowerCase() === this.linkId.toLowerCase();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.load(false));

    this.destroyRef.onDestroy(() => {
      if (this.subscribedTopic) {
        void this.liveUpdateService.unsubscribe(this.subscribedTopic);
        this.subscribedTopic = null;
      }
    });
    void this.setupLiveSubscription();
    this.loadLookups();
    this.load(true);
  }

  campaignName(): string {
    const link = this.link();
    if (!link) {
      return '';
    }
    return this.campaigns().find(item => item.id === link.growthCampaignId)?.name
      ?? this.t('growth.paidMedia.common.unknownCampaign');
  }

  accountName(): string {
    const link = this.link();
    if (!link) {
      return '';
    }
    const account = this.accounts().find(item => item.id === link.accountId);
    return account ? accountDisplayLabel(account) : this.t('growth.paidMedia.common.unknownAccount');
  }

  objectiveLabel(value: string): string {
    return this.t(`growth.paidMedia.objectives.${value}`);
  }

  approvalLabel(value: string): string {
    return this.t(`growth.paidMedia.approvalState.${value}`);
  }

  canSubmit(): boolean {
    const state = this.link()?.approvalState;
    return state === 'Draft' || state === 'Blocked';
  }

  canApprove(): boolean {
    return this.link()?.approvalState === 'PendingApproval';
  }

  openSubmitDialog(): void {
    this.requestedSpend = this.link()?.requestedSpend ?? this.link()?.budgetCap ?? 0;
    this.submitDialogVisible.set(true);
  }

  openApproveDialog(): void {
    this.approveReason = '';
    this.approveDialogVisible.set(true);
  }

  confirmSubmit(): void {
    this.actionType.set('submit');
    this.errorMessage.set('');
    this.successMessage.set('');
    this.api.submitCampaignLink(this.linkId, { requestedSpend: this.requestedSpend })
      .pipe(finalize(() => this.actionType.set(null)))
      .subscribe({
        next: () => {
          this.submitDialogVisible.set(false);
          this.successMessage.set(this.t('growth.paidMedia.campaignLinkDetail.messages.submitSucceeded'));
          this.load(false);
        },
        error: () => this.errorMessage.set(this.t('growth.paidMedia.campaignLinkDetail.messages.submitFailed'))
      });
  }

  confirmApprove(): void {
    this.actionType.set('approve');
    this.errorMessage.set('');
    this.successMessage.set('');
    this.api.approveCampaignLink(this.linkId, { reason: this.approveReason.trim() || null })
      .pipe(finalize(() => this.actionType.set(null)))
      .subscribe({
        next: () => {
          this.approveDialogVisible.set(false);
          this.successMessage.set(this.t('growth.paidMedia.campaignLinkDetail.messages.approveSucceeded'));
          this.load(false);
        },
        error: () => this.errorMessage.set(this.t('growth.paidMedia.campaignLinkDetail.messages.approveFailed'))
      });
  }

  loadLookups(): void {
    this.api.listAccounts().subscribe({
      next: rows => this.accounts.set(rows),
      error: () => this.accounts.set([])
    });
    this.marketing.listCampaigns().subscribe({
      next: rows => this.campaigns.set(rows),
      error: () => this.campaigns.set([])
    });
  }

  load(showLoading = true): void {
    if (!this.linkId) {
      this.loading.set(false);
      this.link.set(null);
      return;
    }
    if (showLoading) {
      this.loading.set(true);
    }
    this.errorMessage.set('');
    this.api.getCampaignLink(this.linkId).subscribe({
      next: row => {
        this.link.set(row);
        this.loading.set(false);
      },
      error: () => {
        this.link.set(null);
        this.loading.set(false);
        this.errorMessage.set(this.t('growth.paidMedia.campaignLinkDetail.messages.loadFailed'));
      }
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private async setupLiveSubscription(): Promise<void> {
    const tenantId = this.context.context()?.tenantId;
    if (!tenantId) {
      return;
    }
    this.subscribedTopic = growthPaidMediaTopic(tenantId);
    await this.liveUpdateService.subscribe(this.subscribedTopic);
  }
}
